/**
 * Checks whether a base64-encoded string represents an SVG image.
 */
export function isSvgIcon(base64: string): boolean {
  try {
    const decoded = atob(base64).trimStart();
    return decoded.startsWith('<svg') || decoded.startsWith('<?xml');
  } catch {
    return false;
  }
}

/**
 * Converts various image formats to a proper data URL for browser display
 *
 * @param icon - The icon/image data which can be:
 *   - A data URL (already formatted)
 *   - A base64 encoded SVG
 *   - A base64 encoded PNG/JPG
 *   - A raw SVG string
 * @returns A properly formatted data URL or undefined if no icon provided
 *
 * @example
 * ```ts
 * // Base64 PNG
 * convertToDataUrl('iVBORw0KGgo...') // Returns: 'data:image/png;base64,iVBORw0KGgo...'
 *
 * // Base64 SVG
 * convertToDataUrl('PHN2ZyB4bWxu...') // Returns: 'data:image/svg+xml;base64,PHN2ZyB4bWxu...'
 *
 * // Raw SVG
 * convertToDataUrl('<svg>...</svg>') // Returns: 'data:image/svg+xml;utf8,...'
 *
 * // Already formatted data URL
 * convertToDataUrl('data:image/png;base64,...') // Returns as-is
 * ```
 */
export function convertToDataUrl(icon: string | undefined): string | undefined {
  if (!icon) return undefined;

  // If it already starts with data:, return as is
  if (icon.startsWith('data:')) {
    return icon;
  }

  // Check if it's an SVG by looking at the beginning of the decoded content
  // SVG base64 strings typically start with:
  // - PHN2ZyB (which is "<svg " in base64)
  // - PD94bWwg (which is "<?xml" in base64)
  if (
    icon.startsWith('PHN2ZyB') ||
    icon.startsWith('PD94bWwg') ||
    icon.startsWith('<svg')
  ) {
    // For SVG, if it's not base64 encoded but raw SVG
    if (icon.startsWith('<svg')) {
      return `data:image/svg+xml;utf8,${encodeURIComponent(icon)}`;
    }
    return `data:image/svg+xml;base64,${icon}`;
  }

  // Default to PNG for other formats
  return `data:image/png;base64,${icon}`;
}
